import {makeAutoObservable, runInAction} from 'mobx';
import moment from 'moment';
import {axiosHandler} from '../network';
import {LocStore} from '../utils';

import shopStore from './shopStore';
import priceStore from './priceStore';
// 은석님 결정(2026-09-14): 같은 매장 최대 10건 · 첫 예약일부터 30일.
// 서버 bundleHold.js·앱 웹뷰와 같은 값이어야 한다 — 화면이 서버보다 좁으면 손님은 그 차이만큼 못 담는다.
export const BUNDLE_MAX_ITEMS = 10;
export const BUNDLE_SPAN_DAYS = 30;
const SPAN_MESSAGE = `함께 담는 예약은 첫 예약일부터 ${BUNDLE_SPAN_DAYS}일 이내여야 합니다. 이후 예약은 따로 결제해주세요.`;
const MAX_ITEMS_MESSAGE = `예약은 최대 ${BUNDLE_MAX_ITEMS}건까지 담을 수 있습니다.`;
const REVIEW_MESSAGE = '결제 확인이 필요합니다. 재결제하지 말고 지점으로 문의해주세요.';
const PENDING_MESSAGE = '결제 확인 중입니다. 잠시 후 재조회해주세요.';
const spanDays = items => {
    const days = items.map(item => moment(item.sdStart).startOf('day'));
    return days.length ? moment.max(days).diff(moment.min(days), 'days') : 0;
};

export const overlaps = (a, b) => String(a.stNo) === String(b.stNo) && a.sdStart < b.sdEnd && b.sdStart < a.sdEnd;
// 서버 bundleHold.normalize 와 같은 규칙(stNo 오름차순, 동률이면 sdStart 오름차순).
// calculate 응답은 서버가 이 순서로 재정렬한 items 를 돌려주므로, 같은 순서로 보내고 같은 순서로 매핑해야 가격이 어긋나지 않는다.
export const sortBundleItems = items => [...items].sort((a, b) => Number(a.stNo) - Number(b.stNo) || String(a.sdStart).localeCompare(String(b.sdStart)));
// 시각 표기가 달라도(Z / .000Z / +09:00) 같은 예약을 같은 키로 본다.
const priceKey = item => `${Number(item.stNo)}@${new Date(item.sdStart).getTime()}`;
export function selectionItem(room, date, times, headcount) {
    if (!date || !times.length) return null;
    const day = moment(date).format('YYYY-MM-DD');
    const start = moment(`${day} ${times[0].start}`, 'YYYY-MM-DD HH:mm');
    const end = moment(`${day} ${times[times.length - 1].end}`, 'YYYY-MM-DD HH:mm');
    if (!end.isAfter(start)) end.add(1, 'day');
    return {stNo: room.stNo, stName: room.stName, sdStart: start.toISOString(), sdEnd: end.toISOString(), headcount};
}
export class BundleStore {
    items = [];
    total = null;
    hold = null;
    result = null;
    busy = false;
    error = '';
    conflicts = [];
    payment = null;
    checkingStatus = false;
    authRequired = false;
    expiryTimer = null;
    constructor() { makeAutoObservable(this, {expiryTimer: false}); }
    get pendingPayment() { return !!this.payment && !this.result; }
    savePayment(data) {
        this.payment = {...this.payment, ...data, items: this.items, total: this.total};
        sessionStorage.setItem('bundlePayment', JSON.stringify(this.payment));
    }
    restorePayment() {
        if (this.payment) return true;
        const saved = sessionStorage.getItem('bundlePayment');
        if (!saved) return false;
        try {
            const payment = JSON.parse(saved);
            if (!payment?.bundle || !Array.isArray(payment.items)) throw new Error('payment');
            this.payment = payment;
            this.items = payment.items;
            this.total = payment.total ?? payment.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
            this.result = payment.result ?? null;
            this.hold = this.result ? null : {bundle: payment.bundle, expireAt: payment.expireAt};
            priceStore.setTotalPriceData({totalPrice: this.total});
        } catch {
            // 읽을 수 없는 복구 정보도 새 결제로 덮어쓰지 않는다.
            this.error = '결제 정보를 확인하지 못했습니다. 재결제하지 말고 지점으로 문의해주세요.';
        }
        return true;
    }
    stopExpiryTimer() { clearTimeout(this.expiryTimer); this.expiryTimer = null; }
    watchPayment(onSuccess) {
        this.stopExpiryTimer();
        const remaining = new Date(this.payment?.expireAt).getTime() - Date.now();
        if (!this.pendingPayment || !(remaining > 0)) return;
        this.expiryTimer = setTimeout(() => {
            runInAction(() => { this.busy = false; });
            this.refreshStatus(onSuccess);
        }, remaining);
    }
    complete(result, onSuccess) {
        if (this.result) return;
        this.stopExpiryTimer();
        this.result = result;
        this.total = result.total ?? this.total;
        this.hold = null;
        this.busy = false;
        this.error = '';
        this.savePayment({state: 'confirmed', result});
        onSuccess?.();
    }
    async refreshStatus(onSuccess) {
        this.restorePayment();
        const bundle = this.payment?.bundle;
        if (!bundle || this.checkingStatus) return;
        if (this.result) { onSuccess?.(); return 'confirmed'; }
        this.checkingStatus = true;
        try {
            const status = await this.post('status', {bundle});
            // 예약 응답과 상태 조회가 교차해도 완료 상태를 되돌리지 않는다.
            if (this.payment?.bundle !== bundle || this.result) return this.result ? 'confirmed' : null;
            runInAction(() => {
                if (status.state === 'confirmed') {
                    this.complete(status.result ?? {bundle, orders: this.items, total: this.total}, onSuccess);
                } else if (status.state === 'released') {
                    this.stopExpiryTimer();
                    this.hold = null;
                    this.payment = null;
                    this.busy = false;
                    sessionStorage.removeItem('bundlePayment');
                    this.error = '결제가 취소되었습니다.';
                } else {
                    this.savePayment({state: status.state, expireAt: status.expireAt ?? this.payment.expireAt});
                    this.hold = {bundle, expireAt: this.payment.expireAt};
                    this.error = status.state === 'review' ? REVIEW_MESSAGE : PENDING_MESSAGE;
                }
            });
            return status.state;
        } catch {
            runInAction(() => { this.error = '결제 결과를 확인하지 못했습니다. 재결제하지 말고 재조회하거나 지점으로 문의해주세요.'; });
        } finally { runInAction(() => { this.checkingStatus = false; }); }
    }
    async finishPayment(response, onSuccess) {
        const bundle = this.payment?.bundle;
        if (!bundle || this.result) return;
        try {
            if (!response.success) {
                await this.post('cancel', {bundle});
                await this.refreshStatus(onSuccess);
            } else {
                const result = await this.post('reservation', {bundle, transactionUid: response.imp_uid});
                if (this.payment?.bundle === bundle) runInAction(() => this.complete(result, onSuccess));
            }
        } catch {
            await this.refreshStatus(onSuccess);
        } finally { runInAction(() => { this.busy = false; }); }
    }
    async post(action, data) {
        let timer;
        let res;
        try {
            const request = axiosHandler({method: 'POST', url: `/rooms/bundle/${action}`, data: {...data, ...(LocStore('userToken') ? {member: LocStore('userToken')} : {})}, token: `Bearer ${LocStore(`shopToken_${shopStore.spno}`)}`});
            res = ['reservation', 'status', 'pay-start'].includes(action) ? await Promise.race([
                request,
                new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('결제 확인 시간 초과')), 15000); }),
            ]) : await request;
        } finally { clearTimeout(timer); }
        const body = res.data;
        if (body.errorCode || (body.resultCode && body.resultCode !== 200) || body.data?.errorCode) throw Object.assign(new Error('묶음 예약 요청에 실패했습니다.'), {response: res});
        return body.data ?? body;
    }
    payload(items = this.items) { return items.map(({stNo, sdStart, sdEnd, headcount}) => ({stNo, sdStart, sdEnd, headcount})); }
    async update(items) {
        if (this.busy || this.hold || this.restorePayment()) return;
        this.busy = true;
        this.error = '';
        const sorted = sortBundleItems(items);
        try {
            const quote = sorted.length ? await this.post('calculate', {items: this.payload(sorted)}) : {items: [], total: null};
            const quoted = quote.items ?? [];
            if (sorted.length !== quoted.length) throw new Error('quote size mismatch');
            // 응답에 sdStart 가 있으면 (stNo, sdStart) 키로 매핑한다 — 같은 방 다른 시간 2건은 stNo 만으로는 구별되지 않는다.
            // 없으면(현재 서버) 정렬 순서 인덱스로 매핑하고, 그마저 어긋나면 틀린 가격을 조용히 보여주지 않고 오류로 처리한다.
            let prices;
            if (quoted.every(entry => entry.sdStart != null)) {
                const byKey = new Map(quoted.map(entry => [priceKey(entry), entry.price]));
                if (byKey.size !== quoted.length || sorted.some(item => !byKey.has(priceKey(item)))) throw new Error('quote key mismatch');
                prices = sorted.map(item => byKey.get(priceKey(item)));
            } else {
                if (sorted.some((item, i) => String(item.stNo) !== String(quoted[i].stNo))) throw new Error('quote order mismatch');
                prices = quoted.map(entry => entry.price);
            }
            runInAction(() => {
                this.items = sorted.map((item, i) => ({...item, price: prices[i]}));
                this.total = quote.total;
                priceStore.setTotalPriceData({totalPrice: this.total ?? 0});
                this.conflicts = [];
            });
        } catch { runInAction(() => { this.error = '금액을 확인하지 못했습니다. 다시 시도해주세요.'; }); }
        finally { runInAction(() => { this.busy = false; }); }
    }
    async add(item) {
        if (this.items.length >= BUNDLE_MAX_ITEMS) { this.error = MAX_ITEMS_MESSAGE; return; }
        if (!item || this.items.some(other => overlaps(item, other))) { this.error = '이미 담긴 시간입니다.'; return; }
        if (spanDays([...this.items, item]) > BUNDLE_SPAN_DAYS) { this.error = SPAN_MESSAGE; return; }
        await this.update([...this.items, item]);
    }
    remove(index) { return this.update(this.items.filter((_, i) => i !== index)); }
    validate() {
        if (this.items.length > BUNDLE_MAX_ITEMS) { this.error = MAX_ITEMS_MESSAGE; return false; }
        if (spanDays(this.items) > BUNDLE_SPAN_DAYS) { this.error = SPAN_MESSAGE; return false; }
        if (this.items.some((item, i) => this.items.slice(0, i).some(other => overlaps(item, other)))) {
            this.error = '이미 담긴 시간입니다.'; return false;
        }
        this.error = '';
        return this.items.length > 0;
    }
    requireAuthentication() {
        localStorage.removeItem('userToken');
        this.authRequired = true;
        this.error = '인증이 만료되었습니다. 휴대폰 인증을 다시 진행해주세요.';
    }
    async check() {
        if (this.restorePayment()) return false;
        this.authRequired = false;
        if (!LocStore('userToken')) { this.error = '휴대폰 인증을 완료해주세요.'; return false; }
        if (!this.items.length || this.busy) return false;
        this.busy = true;
        this.error = '';
        try {
            const hold = await this.post('check', {items: this.payload()});
            runInAction(() => { this.hold = hold; this.total = hold.total; priceStore.setTotalPriceData({totalPrice: this.total}); });
            return true;
        } catch (error) {
            const body = error.response?.data;
            const info = body?.data ?? body;
            if ([error.response?.status, body?.resultCode, info?.resultCode].some(code => [401, 403].includes(Number(code)))) {
                this.requireAuthentication();
                return false;
            }
            const conflict = error.response?.status === 409 || body?.resultCode === 409 || info?.errorCode === 'CONFLICT_BUNDLE_RESERVATION';
            let conflicts = info?.items ?? info?.conflicts ?? [];
            if (conflict && !conflicts.length) {
                for (const item of this.items) {
                    try {
                        const probe = await this.post('check', {items: this.payload([item])});
                        await this.post('cancel', {bundle: probe.bundle});
                    } catch (e) {
                        if (e.response?.status === 409 || e.response?.data?.resultCode === 409 || (e.response?.data?.data ?? e.response?.data)?.errorCode === 'CONFLICT_BUNDLE_RESERVATION') conflicts.push(item);
                    }
                }
            }
            runInAction(() => { this.conflicts = conflicts; this.error = conflict ? '예약이 겹치는 항목을 삭제하고 다시 시도해주세요.' : '예약을 확인하지 못했습니다. 다시 시도해주세요.'; });
            return false;
        } finally { runInAction(() => { this.busy = false; }); }
    }
    async cancel(onSuccess) {
        if (this.restorePayment()) return this.refreshStatus(onSuccess);
        if (!this.hold) return;
        const bundle = this.hold.bundle;
        const expireAt = this.hold.expireAt;
        const response = await this.post('cancel', {bundle});
        // 서버(services/bundle.js cancel)는 취소 직전에 결제가 paid 로 확인되면 취소 대신 확정 처리하고
        // {released:false, state:'confirmed', result}를 돌려준다. hold 만 지우면 "취소했는데 결제됐다"가 되므로 완료 경로로 전이한다.
        if (response?.state === 'confirmed') {
            runInAction(() => {
                this.savePayment({bundle, expireAt});
                this.complete(response.result ?? {bundle, orders: this.items, total: this.total}, onSuccess);
            });
            return 'confirmed';
        }
        // released !== true 면(review·approving·보호된 묶음) 예약이 아직 살아 있다 — hold 를 지워 취소된 것처럼 보이게 하지 않고
        // 기존 결제 확인 화면(BundlePayment)으로 보낸다. 이어지는 status 조회가 실제 상태로 정정한다.
        if (response?.released !== true) {
            const state = response?.state ?? 'pending';
            runInAction(() => {
                this.savePayment({bundle, expireAt, state});
                this.error = state === 'review' ? REVIEW_MESSAGE : PENDING_MESSAGE;
            });
            return state;
        }
        runInAction(() => { this.hold = null; });
    }
    async pay(IMP, buyer, onSuccess) {
        if (this.busy) return;
        if (this.restorePayment()) return this.refreshStatus(onSuccess);
        if (!this.hold || new Date(this.hold.expireAt).getTime() <= Date.now()) { this.error = '예약 시간이 만료되었습니다. 뒤로 돌아가 다시 예약해주세요.'; return; }
        this.busy = true;
        this.error = '';
        let handled = false;
        // pay-start 응답이 유실되어도 같은 주문으로 상태를 확인할 수 있어야 한다.
        this.savePayment({...this.hold, state: 'paying'});
        this.watchPayment(onSuccess);
        try {
            const pg = process.env.REACT_APP_IMPORT_CHANNEL || 'kcp.IP0J1';
            const payment = await this.post('pay-start', {bundle: this.hold.bundle, pg, payMethod: 'card'});
            runInAction(() => {
                this.hold.expireAt = payment.expireAt;
                this.savePayment({bundle: payment.bundle, expireAt: payment.expireAt});
                this.watchPayment(onSuccess);
            });
            if (process.env.REACT_APP_IMPORT_NUM) IMP.init(process.env.REACT_APP_IMPORT_NUM);
            IMP.request_pay({...buyer, pg, pay_method: 'card', merchant_uid: payment.bundle, amount: payment.amount, name: payment.name}, async response => {
                if (handled || this.payment?.bundle !== payment.bundle) return;
                handled = true;
                await this.finishPayment(response, onSuccess);
            });
        } catch {
            runInAction(() => { this.busy = false; });
            await this.refreshStatus(onSuccess);
        }
    }
    reset() {
        this.stopExpiryTimer();
        sessionStorage.removeItem('bundlePayment');
        this.items = []; this.total = null; this.hold = null; this.result = null; this.payment = null;
        this.busy = false; this.checkingStatus = false; this.authRequired = false; this.error = ''; this.conflicts = [];
        priceStore.setTotalPriceData({totalPrice: 0});
    }
}
const bundleStore = new BundleStore();
export default bundleStore;
